import { useEffect } from "react";
import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";

const MUTED = "#94a3b8";
const BORDER = "rgba(99,102,241,0.15)";
const DIVIDER = { height: "1px", background: "linear-gradient(to right, transparent, rgba(99,102,241,0.25), transparent)" };

function Section({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <section data-testid={`section-terms-${number}`}>
      <h2 className="text-base font-semibold mb-3 text-white">{number}. {title}</h2>
      <div className="space-y-3" style={{ color: MUTED }}>
        {children}
      </div>
    </section>
  );
}

export default function Terms() {
  useEffect(() => {
    document.title = "Terms of Service | Vexis";
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#05070a", color: "#ffffff" }}>
      <nav className="sticky top-0 z-50" style={{ backgroundColor: "rgba(5,7,10,0.92)", borderBottom: `1px solid ${BORDER}`, backdropFilter: "blur(20px)" }}>
        <div className="container mx-auto px-4 py-3 flex items-center gap-4">
          <Link href="/">
            <button className="flex items-center gap-2 text-sm font-medium transition-colors px-3 py-2 rounded-lg hover:bg-white/5" style={{ color: MUTED }}>
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
          </Link>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-10 md:py-14 max-w-3xl">
        <div className="mb-10">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-3" style={{ color: "#6366f1" }}>Legal</p>
          <h1 className="text-3xl font-bold text-white mb-2">Terms of Service</h1>
          <p className="text-base font-medium text-white">LHA Holdings Ltd trading as Vexis Talent Agency</p>
          <p className="text-sm mt-1" style={{ color: MUTED }}>Last updated: April 2026</p>
        </div>

        <div className="space-y-8 text-base leading-relaxed">
          <Section number="1" title="Agreement to Terms">
            <p>
              By accessing or using the Vexis platform ("the Platform"), you agree to be bound by these Terms of
              Service ("Terms"). These Terms apply to all users of the Platform including recruitment agencies,
              individual recruiters, and any person accessing a candidate assessment link. If you do not agree to
              these Terms, do not use the Platform.
            </p>
            <p>
              The Platform is operated by LHA Holdings Ltd trading as Vexis Talent Agency, registered in New
              Zealand ("Vexis", "we", "us", "our").
            </p>
          </Section>

          <div style={DIVIDER} />

          <Section number="2" title="Description of Service">
            <p>
              Vexis provides a recruitment assessment SaaS platform that enables recruiters to send structured video
              assessments to candidates, receive AI-assisted scoring and triage, and access human-reviewed candidate
              dossiers written back to their applicant tracking system.
            </p>
            <p>
              The Platform is provided on a subscription basis. Access is granted to authorised recruitment
              professionals ("Recruiters") who have connected their applicant tracking system or registered
              directly. Candidates access the Platform via a secure token link provided by a Recruiter and do not
              hold accounts on the Platform.
            </p>
          </Section>

          <div style={DIVIDER} />

          <Section number="3" title="Subscriptions and Payment">
            <p>
              Access to paid features of the Platform requires a valid subscription. Subscriptions are billed
              monthly in New Zealand Dollars (NZD) at the rates published on the pricing page at the time of
              subscription. Current pricing: Free ($0), Starter ($99 NZD/month), Professional ($299 NZD/month),
              Enterprise (contact us).
            </p>
            <p>
              Payments are processed by Stripe. By subscribing, you authorise Vexis to charge your nominated
              payment method on a recurring monthly basis until cancelled.
            </p>
            <p>
              Subscriptions may be cancelled at any time through the billing portal. Cancellation takes effect at
              the end of the current billing period. No refunds are issued for partial months. Vexis reserves the
              right to change pricing with 30 days written notice to active subscribers.
            </p>
            <p>
              The Free plan is available without payment and is subject to a limit of 10 active candidates. Vexis
              reserves the right to modify Free plan limits at any time.
            </p>
          </Section>

          <div style={DIVIDER} />

          <Section number="4" title="Acceptable Use">
            <p>You agree to use the Platform only for lawful recruitment purposes. You must not use the Platform to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Discriminate against candidates on the basis of any protected characteristic under applicable law</li>
              <li>Collect candidate data for purposes other than the specific recruitment process for which the assessment was issued</li>
              <li>Share candidate dossiers with parties not involved in the relevant hiring decision</li>
              <li>Attempt to reverse-engineer, copy, or replicate any part of the Platform</li>
              <li>Send assessment links to individuals who have not applied for or expressed interest in the relevant role</li>
              <li>Use the Platform in any way that violates applicable employment, privacy, or anti-discrimination law</li>
            </ul>
          </Section>

          <div style={DIVIDER} />

          <Section number="5" title="AI Scoring and Human Review">
            <p>
              The Platform uses artificial intelligence to transcribe candidate video responses and produce a
              preliminary score and triage band. This AI output is a triage tool only and does not constitute a
              hiring recommendation or decision.
            </p>
            <p>
              Every candidate submission is reviewed by a human agent before any outcome is set. No candidate is
              rejected or progressed by an automated system alone. The human-reviewed assessment is the
              authoritative output of the Platform.
            </p>
            <p>
              Recruiters retain full responsibility for all hiring decisions. Vexis does not make hiring decisions
              and accepts no liability for outcomes resulting from a Recruiter's use of Platform outputs.
            </p>
          </Section>

          <div style={DIVIDER} />

          <Section number="6" title="Candidate Data and Privacy">
            <p>
              Recruiters are responsible for obtaining any consents required by applicable law before sending a
              candidate an assessment link. By using the Platform to send an assessment, the Recruiter warrants
              that the candidate has applied for or expressed genuine interest in the relevant role.
            </p>
            <p>
              Candidate video recordings are deleted automatically within 24 hours of assessment completion.
              Assessment scores, transcripts, and dossiers are retained for the duration of the Recruiter's
              subscription and deleted within 30 days of a valid deletion request.
            </p>
            <p>
              Vexis processes candidate data as a data processor on behalf of the Recruiter (the data controller)
              for the purposes of providing the Platform. Our Privacy Policy at vexistalentagency.com/privacy sets
              out how we handle personal data.
            </p>
          </Section>

          <div style={DIVIDER} />

          <Section number="7" title="Intellectual Property">
            <p>
              All Platform content, design, code, templates, and AI-generated outputs belong to LHA Holdings Ltd
              or its licensors. Candidate dossiers generated by the Platform are provided to Recruiters for use in
              their recruitment process only and may not be resold, redistributed, or used for any other purpose.
            </p>
            <p>
              Recruiters retain ownership of the candidate data they input into the Platform. By using the
              Platform, Recruiters grant Vexis a limited licence to process that data for the purpose of providing
              the service.
            </p>
          </Section>

          <div style={DIVIDER} />

          <Section number="8" title="Limitation of Liability">
            <p>
              To the maximum extent permitted by law, Vexis shall not be liable for any indirect, incidental,
              special, or consequential loss arising from use of the Platform, including but not limited to: loss
              of revenue, loss of candidates, hiring decisions made on the basis of Platform outputs, or any claim
              by a candidate arising from their assessment experience.
            </p>
            <p>
              Our total liability to any Recruiter in any 12-month period shall not exceed the total subscription
              fees paid by that Recruiter in that period.
            </p>
            <p>
              Nothing in these Terms excludes liability for fraud, death, or personal injury caused by our
              negligence, or any other liability that cannot be excluded by law.
            </p>
          </Section>

          <div style={DIVIDER} />

          <Section number="9" title="Termination">
            <p>
              Vexis may suspend or terminate access to the Platform immediately if a Recruiter breaches these
              Terms, fails to pay subscription fees, or uses the Platform in a manner that creates legal or
              reputational risk for Vexis. On termination, the Recruiter's workspace data will be retained for 30
              days and then permanently deleted.
            </p>
            <p>Recruiters may terminate their subscription at any time through the billing portal.</p>
          </Section>

          <div style={DIVIDER} />

          <Section number="10" title="Governing Law">
            <p>
              These Terms are governed by the laws of New Zealand. Any dispute arising from these Terms shall be
              subject to the exclusive jurisdiction of the New Zealand courts.
            </p>
          </Section>

          <div style={DIVIDER} />

          <Section number="11" title="Changes to These Terms">
            <p>
              Vexis may update these Terms from time to time. Material changes will be notified to active
              subscribers by email at least 14 days before taking effect. Continued use of the Platform after
              changes take effect constitutes acceptance of the updated Terms.
            </p>
          </Section>

          <div style={DIVIDER} />

          <Section number="12" title="Contact">
            <p>For questions about these Terms contact: <a href="mailto:legal@vexistalentagency.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">legal@vexistalentagency.com</a></p>
            <p>For privacy matters: <a href="mailto:privacy@vexistalentagency.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">privacy@vexistalentagency.com</a></p>
            <p>For general enquiries: <a href="mailto:hello@vexistalentagency.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">hello@vexistalentagency.com</a></p>
            <p className="pt-2">LHA Holdings Ltd trading as Vexis Talent Agency<br />vexistalentagency.com</p>
          </Section>
        </div>
      </main>

      <footer className="py-8 mt-12" style={{ borderTop: `1px solid ${BORDER}` }}>
        <div className="container mx-auto px-4 text-center text-sm" style={{ color: MUTED }}>
          <p>&copy; 2026 Vexis Talent Agency</p>
          <p className="text-xs mt-1" style={{ color: "#64748b" }}>Vexis Talent Agency is a trading name of LHA Holdings Ltd.</p>
          <div className="flex justify-center gap-6 mt-3 flex-wrap">
            <Link href="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link href="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link href="/security" className="hover:text-white transition-colors">Security</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
